import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';

type GeoMode = 'india' | 'international';

interface GeoOverrideContextType {
  geoMode: GeoMode | null; // null = no override, use auto-detect
  toggleGeo: () => void;
  isOverridden: boolean;
}

const STORAGE_KEY = 'admin-geo-override';
const GeoOverrideContext = createContext<GeoOverrideContextType>({
  geoMode: null,
  toggleGeo: () => {},
  isOverridden: false,
});

function getStored(): GeoMode | null {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (v === 'india' || v === 'international') return v;
  } catch {}
  return null;
}

export function GeoOverrideProvider({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth();
  const [geoMode, setGeoMode] = useState<GeoMode | null>(isAdmin ? getStored() : null);

  const toggleGeo = useCallback(() => {
    if (!isAdmin) return;
    const next: GeoMode = geoMode === 'international' ? 'india' : 'international';
    sessionStorage.setItem(STORAGE_KEY, next);
    setGeoMode(next);
  }, [isAdmin, geoMode]);

  return (
    <GeoOverrideContext.Provider value={{ geoMode: isAdmin ? geoMode : null, toggleGeo, isOverridden: isAdmin && geoMode !== null }}>
      {children}
    </GeoOverrideContext.Provider>
  );
}

export function useGeoOverride() {
  return useContext(GeoOverrideContext);
}
